import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import useApi from '../api';
import { setUser } from '../store/reducers/auth';
import { addExtensionEndpoint, extensionEnabled } from '../store/reducers/menu';
import { AuthState, Store } from 'store/types';
import { applyBranding } from '../store/reducers/settings';
import { FileMetadata } from '../api/types/cms';
import { matchPath } from 'react-router';
import { useTranslation } from './TranslationContext';
import { Locale } from '../api/types/translation';

const useApiDataHook = () => {
    const api = useApi();
    const { loadTranslations } = useTranslation();
    const [apiData, setApiData] = useState();
    const [userData, setUserData] = useState();
    const [loadComplete, setLoadComplete] = useState(false);

    const updateApiData = (response: any) => {
        if (response && !response.error) {
            setApiData(response);
        }
    };

    const updateUserData = (response: any) => {
        if (response && !response.error) {
            setUserData(response);
        }
    };

    const loadData = useCallback(() => {
        setLoadComplete(false);
        Promise.all([
            loadTranslations(),
            api.info.getOverview(),
            api.user.getOwnData(),
        ]).then(values => {
            updateApiData(values[1]);
            updateUserData(values[2]);
            setLoadComplete(true);
        });
    }, [api]);

    return useMemo(() => {
        return {
            loadData,
            loadComplete,
            data: {
                apiData,
                userData,
            },
        };
    }, [loadComplete]);
};

const useExtensionLoader = () => {
    const supportedExtensions = [
        'qlive_core_admin',
        'qlive_core_cms',
        'qlive_partner',
        'qlive_cronjob',
        'qlive_chat',
        'qlive_property_management',
        'qlive_thriftshop',
    ];
    const dispatch = useDispatch();

    const load = (apiData: {
        enabled_extensions: Array<string>;
        endpoints: Object;
        system?: {
            system_name: string;
            primary_color: string;
            secondary_color: string;
            frontend_info_endpoint?: string;
            logo?: FileMetadata;
            dark_mode: boolean;
            locales: Array<Locale>;
        };
    }) => {
        if (apiData.system) {
            document.title = `${apiData.system.system_name} [Q.Live]`;
            dispatch(applyBranding(apiData.system));
        }

        for (const extension of apiData.enabled_extensions) {
            if (supportedExtensions.includes(extension)) {
                dispatch(
                    extensionEnabled({
                        name: extension,
                    }),
                );
            }
        }
        for (const endpointName of Object.keys(apiData.endpoints)) {
            dispatch(addExtensionEndpoint(endpointName));
        }
    };

    return { loadExtensions: load };
};

type RefreshContextType = {
    refreshOwnData: () => Promise<boolean>;
};

const defaultRefreshContext: RefreshContextType = {
    refreshOwnData: async () => false,
};

const RefreshContext = createContext<RefreshContextType>(defaultRefreshContext);

export const useRefreshContext = () => {
    return useContext(RefreshContext);
};

const AuthContext = ({ children }: PropsWithChildren) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { pathname } = useLocation();

    const { isAuthenticated } = useSelector<Store, AuthState>(
        state => state.auth,
    );
    const { data, loadComplete, loadData } = useApiDataHook();
    const { loadExtensions } = useExtensionLoader();

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isAuthenticated) {
            loadData();
        } else {
            if (pathname !== '/login') {
                navigate('/login');
            }
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (loadComplete && data.apiData && data.userData) {
            loadExtensions(data.apiData);
            dispatch(setUser(data.userData));
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [loadComplete]);

    useEffect(() => {
        if (!loading) {
            const query = new URL(window.location.href);
            const redirect = query.searchParams.get('r');
            if (redirect) {
                const match = matchPath(redirect, pathname);
                if (match) {
                    navigate(match.pathname);
                } else {
                    navigate('/');
                }
            } else {
                if (isAuthenticated && pathname === '/login') {
                    navigate('/');
                }
            }
        }
    }, [loading, pathname]);

    const refreshOwnData = (): Promise<boolean> => {
        return new Promise(resolve => {
            try {
                loadData();
                resolve(true);
            } catch (e) {
                resolve(false);
            }
        });
    };

    return useMemo(() => {
        console.debug('qlive:authenticated', isAuthenticated);

        return (
            <RefreshContext.Provider value={{ refreshOwnData }}>
                {children}
            </RefreshContext.Provider>
        );
    }, [isAuthenticated]);
};

AuthContext.propTypes = {
    children: PropTypes.node,
};

export default AuthContext;
